import { AnyRestable } from '../../../../types';
import { FetchError, NetworkFetchError } from '../../errors';
import { TimeoutError } from '../../errors/TimeoutError';
import { SafeLogErrorOptions } from '../../log-error';
import pause from '../../pause';
import { isErrorAnswer } from './helpers';

export interface RetryOptions {
    retryDelay?: number;
    retryCount?: number;
}

export type BodyParseMethod = 'json' | 'blob' | 'text';

interface ApiOptions extends RetryOptions {
    modifyResponse?(response: Response): AnyRestable;
    timeout?: number;
    bodyParseMethod?: BodyParseMethod;
}

export const api = <T>(
    url: string,
    request: RequestInit = {},
    options: ApiOptions = {},
    loggerFields: SafeLogErrorOptions = { method: 'api' }
) => {
    const { retryCount = 0, retryDelay = 1000, timeout, modifyResponse, bodyParseMethod = 'json' } = options;
    const requestInfo: RequestInit = {
        credentials: 'include',
        ...request,
    };
    const fetchWithTimeout = (url: string, requestInfo: RequestInit) => {
        const abortController = new AbortController();
        const signal = abortController.signal;
        return new Promise<Response>((resolve, reject) => {
            let timeoutId: ReturnType<typeof setTimeout> | undefined;
            if (typeof timeout === 'number') {
                timeoutId = setTimeout(() => {
                    abortController.abort();
                }, timeout);
            }

            fetch(url, { ...requestInfo, signal })
                .then((result) => {
                    resolve(result);
                })
                .catch((error) => {
                    if (error.name === 'AbortError') {
                        reject(
                            new TimeoutError({
                                ...loggerFields,
                            })
                        );
                    } else {
                        reject(error);
                    }
                })
                .finally(() => {
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore - почему-то ругается на тип timeoutId, хотя он вроде норм объявлен
                    clearTimeout(timeoutId);
                });
        });
    };

    const fetchRetry = (url: string, requestOptions: RequestInit, tries: number): Promise<T> => {
        let triesLeft: number = tries;

        const onFetchError = (error: Error) => {
            triesLeft--;

            if (triesLeft <= 0 || !retryDelay || (FetchError.is(error) && error.status >= 400 && error.status < 500)) {
                if (!FetchError.is(error)) {
                    throw new NetworkFetchError({
                        statusText: error.message,
                        error: error.message,
                        requestUrl: url,
                        fetchRetryN: tries - triesLeft,
                        loggerFields,
                    });
                }

                throw error;
            } else {
                return pause(retryDelay).then(() => fetchRetry(url, requestOptions, triesLeft));
            }
        };
        const responsePromise =
            typeof timeout === 'number' ? fetchWithTimeout(url, requestInfo) : fetch(url, requestInfo);
        return responsePromise
            .then(async (result) => {
                if (!result.ok) {
                    const errorAnswer = bodyParseMethod === 'json' ? await result.json() : '';
                    const { status, statusText } = result;

                    throw new FetchError({
                        status,
                        statusText,
                        error: isErrorAnswer(errorAnswer)
                            ? errorAnswer.error || errorAnswer.description // error лучше, т.к. это id ошибки, но error есть не во всех ответах
                            : String(errorAnswer),
                        requestUrl: url,
                        fetchRetryN: tries - triesLeft,
                        loggerFields,
                    });
                }

                let res = { json: {}, blob: new Blob(), text: '' }[bodyParseMethod] as Response;
                try {
                    res = await result[bodyParseMethod]();
                } catch {
                    // do nothing
                }

                if (bodyParseMethod === 'json') {
                    return {
                        ...res,
                        ...modifyResponse?.(result),
                    } as T;
                } else {
                    return res as T;
                }
            })
            .catch(onFetchError);
    };

    return fetchRetry(url, requestInfo, retryCount);
};
